// Dynamic wikis: a wiki ("Wiki", e.g. "Regelwerk") is a WikiPage with name, Material Symbols icon and position
// (home quick access section "Wikis", page /wiki/{id}); its entries ("Einträge") are WikiEntries with title,
// content, priority (1 normal, 2 wichtig) and order number. GET /WikiPage/GetAll delivers everything at once
// (useWikis). Limits, defaults and messages mirror WikiService; the server validates again.

export const WIKI_NAME_MAX = 100;
export const WIKI_ICON_MAX = 50;
export const WIKI_DEFAULT_ICON = 'menu_book';
export const WIKI_ENTRY_TITLE_MAX = 500;
export const WIKI_PRIORITY_NORMAL = 1;
export const WIKI_PRIORITY_IMPORTANT = 2;
export const WIKI_ICON_OVERVIEW_URL = 'https://fonts.google.com/icons?icon.style=Rounded';

const ICON_PATTERN = /^[a-z0-9_]{1,50}$/;

export const WIKI_MESSAGES = {
  nameRequired: 'Bitte einen Namen angeben.',
  nameTooLong: `Der Name darf höchstens ${WIKI_NAME_MAX} Zeichen lang sein.`,
  nameDuplicate: 'Ein Wiki mit diesem Namen existiert bereits.',
  iconInvalid: 'Das Icon muss ein Material-Symbols-Name sein (nur Kleinbuchstaben, Ziffern und _).',
  entryTitleRequired: 'Bitte einen Titel angeben.',
  entryTitleTooLong: `Der Titel darf höchstens ${WIKI_ENTRY_TITLE_MAX} Zeichen lang sein.`,
  entryContentRequired: 'Bitte eine Beschreibung angeben.',
};

// Icon as the server stores it: trimmed, lower case, empty → menu_book.
export const normalizeWikiIcon = (value) => {
  const icon = String(value ?? '').trim().toLowerCase();
  return icon || WIKI_DEFAULT_ICON;
};

// Live input of the icon field: lower case, inner blanks become underscores ("Menu Book" → "menu_book").
// Leading blanks are dropped and trailing ones stay blanks, so normalizeWikiIcon still trims them away.
export const wikiIconInput = (value) =>
  String(value ?? '').toLowerCase().replace(/^\s+/, '').replace(/\s+(?=\S)/g, '_');

export const isValidWikiIcon = (value) => ICON_PATTERN.test(normalizeWikiIcon(value));

export const isSameWikiName = (a, b) =>
  String(a ?? '').trim().toLocaleLowerCase('de') === String(b ?? '').trim().toLocaleLowerCase('de');

const byPositionThenId = (a, b) => (a.position - b.position) || (a.id - b.id);
const byOrderThenId = (a, b) => (a.orderNumber - b.orderNumber) || (a.id - b.id);

export function normalizeWikiEntry(raw, wikiPageId) {
  const priority = Number(raw?.priority) === WIKI_PRIORITY_IMPORTANT ? WIKI_PRIORITY_IMPORTANT : WIKI_PRIORITY_NORMAL;
  return {
    id: Number(raw?.id),
    wikiPageId: Number(raw?.wikiPageId ?? wikiPageId),
    title: raw?.title ?? '',
    content: raw?.content ?? '',
    priority,
    orderNumber: Number(raw?.orderNumber ?? 0),
  };
}

export const sortWikiEntries = (entries, wikiPageId) =>
  (Array.isArray(entries) ? entries : [])
    .filter(e => e && e.id != null)
    .map(e => normalizeWikiEntry(e, wikiPageId))
    .sort(byOrderThenId);

export function normalizeWikiPage(raw) {
  const id = Number(raw?.id);
  return {
    id,
    name: raw?.name ?? '',
    icon: normalizeWikiIcon(raw?.icon),
    position: Number(raw?.position ?? 0),
    entries: sortWikiEntries(raw?.entries, id),
  };
}

// GET /WikiPage/GetAll → pages ordered by (Position, Id), entries by (OrderNumber, Id).
export const normalizeWikis = (data) =>
  (Array.isArray(data) ? data : [])
    .filter(p => p && p.id != null)
    .map(normalizeWikiPage)
    .sort(byPositionThenId);

const renumber = (list) => list.map((p, i) => (p.position === i + 1 ? p : { ...p, position: i + 1 }));

// Same algorithm as WikiService (the client patches its list without refetching): order by (Position, Id),
// take the page out, insert it at clamp(Position, 1, count + 1) - 1 (Position <= 0 → end), renumber 1..n.
export function placeWikiPage(list, page) {
  const rest = [...list].sort(byPositionThenId).filter(p => p.id !== page.id);
  const count = rest.length;
  const position = Number(page.position);
  const slot = !Number.isFinite(position) || position <= 0 ? count + 1 : Math.min(Math.max(Math.trunc(position), 1), count + 1);
  rest.splice(slot - 1, 0, page);
  return renumber(rest);
}

// Delete: the remaining pages keep their order and are renumbered 1..n.
export const removeWikiPageFrom = (list, id) =>
  renumber([...list].sort(byPositionThenId).filter(p => p.id !== Number(id)));

// "Noch keine Einträge" / "1 Eintrag" / "5 Einträge"
export function wikiEntryCountLabel(count) {
  const n = Number(count) || 0;
  if (n === 0) return 'Noch keine Einträge';
  return n === 1 ? '1 Eintrag' : `${n} Einträge`;
}

// Client checks of the wiki editor (same texts as WikiService). others: the other wikis (duplicate check).
export function validateWikiForm(form, others) {
  const errors = {};
  const name = String(form.name ?? '').trim();
  if (!name) errors.name = WIKI_MESSAGES.nameRequired;
  else if (name.length > WIKI_NAME_MAX) errors.name = WIKI_MESSAGES.nameTooLong;
  else if ((others ?? []).some(w => isSameWikiName(w.name, name))) errors.name = WIKI_MESSAGES.nameDuplicate;
  if (!isValidWikiIcon(form.icon)) errors.icon = WIKI_MESSAGES.iconInvalid;
  return errors;
}

// Client checks of an entry (create form + edit popup), same texts as WikiService.
export function validateWikiEntry({ title, content }) {
  const trimmed = String(title ?? '').trim();
  if (!trimmed) return WIKI_MESSAGES.entryTitleRequired;
  if (trimmed.length > WIKI_ENTRY_TITLE_MAX) return WIKI_MESSAGES.entryTitleTooLong;
  if (!String(content ?? '').trim()) return WIKI_MESSAGES.entryContentRequired;
  return null;
}

// API error text for display (plain-text 400 bodies); never renders a JSON body.
export function wikiApiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.errors) return Object.values(data.errors).flat().join(', ');
  if (data?.title && err?.response?.status !== 404) return data.title;
  if (!err?.response) return 'Keine Verbindung – bitte später erneut versuchen.';
  if (err.response.status === 403) return 'Keine Berechtigung – nur Admins können Wikis bearbeiten.';
  return fallback;
}
